/**
 * App-root.
 *
 * Verantwoordelijkheden:
 *  1. Auth-hydratie bij boot — useAuthMe() vult de Zustand-store + de
 *     query-cache. Routes mogen pas renderen na de eerste 401-of-200
 *     poging anders flikkert het login-scherm.
 *  2. Tenant-bootstrap (alleen tenant-shell): fetch `/api/v1/tenant`
 *     via useTenantMeta, pas branding toe (kleuren + favicon + manifest),
 *     synchroniseer UI-locale met tenant's allowed list, en laad de
 *     publieke locale-overrides (ADR-0021) zodat het login-scherm al
 *     getheme'd is.
 *  3. 401-interceptor's navigate-callback installeren.
 *  4. Router opzetten + outlet binnen AppShell.
 */
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useAuthMe } from '@shared/hooks/useAuthMe';
import { useTenantMeta } from '@shared/hooks/useTenantMeta';
import { useHandoffConsume } from '@shared/hooks/useHandoffConsume';
import { useLocaleOverridesStore } from '@shared/stores/localeOverrides';
import { useThemeStore } from '@shared/stores/theme';
import { applyBranding, type TenantBranding } from '@shared/branding';
import { useAppMode } from '@shared/app-mode-context';
import { setAuthErrorNavigator } from '@shared/api/client';
import { AppRoutes } from './router/routes';

export function App() {
  const navigate = useNavigate();
  const mode = useAppMode();
  const { t, i18n } = useTranslation();

  // Cross-host impersonatie (ADR-0028): als de app-host geopend is met
  // `?handoff=<token>`, wissel die eerst in tegen een shadow-sessie vóór we
  // de routes (en dus het login-scherm) tonen.
  const { consuming } = useHandoffConsume(mode === 'tenant');

  // 401-redirector koppelen — interceptor heeft een navigate-callback
  // nodig om binnen de SPA te navigeren (geen full page reload bij
  // sessie-verloop).
  useEffect(() => {
    setAuthErrorNavigator((path) => navigate(path, { replace: true }));
  }, [navigate]);

  // Eerste fetchMe-call bij boot. `enabled` is altijd true: we willen
  // weten of de user nog een geldige sessie heeft.
  useAuthMe(true);

  // Tenant-bootstrap. `/api/v1/tenant` is een publiek endpoint dat ook
  // pre-login werkt zodat het login-scherm al de juiste branding krijgt.
  // Op de superadmin-shell bestaat dit endpoint niet — disable de query.
  const tenantMetaQuery = useTenantMeta(mode === 'tenant');

  // Branding + tab-title + locale-snap zodra meta binnen is. Re-runt bij
  // iedere meta-wijziging (bv. superadmin past branding aan en de
  // tenant-admin refresht).
  useEffect(() => {
    if (mode !== 'tenant') return;
    const meta = tenantMetaQuery.data;
    if (!meta) return;

    applyBranding(meta.branding as TenantBranding | null, { tenantName: meta.name });

    const portalName = meta.branding?.name_in_ui ?? meta.name;
    if (portalName) document.title = portalName;

    // Locale-restriction: snap naar tenant's default als de huidige UI-
    // locale niet meer in `available_locales` zit (bv. tenant heeft
    // EN uitgezet terwijl de browser nog op EN stond).
    const allowed = meta.available_locales ?? [];
    if (allowed.length > 0 && !allowed.includes(i18n.language)) {
      const fallback = meta.locale ?? allowed[0];
      void i18n.changeLanguage(fallback);
    }

    // Thema-beleid (issue #95): licht/donker afgedwongen door de tenant, of
    // 'switch' (gebruikerskeuze; zonder keuze volgt de systeemvoorkeur).
    // Pre-login werkt dit ook — de meta is publiek.
    useThemeStore.getState().setMode(meta.theme_mode ?? 'switch');
  }, [mode, tenantMetaQuery.data, i18n]);

  // Superadmin-shell: geen tenant-beleid → de schakelaar is er altijd
  // ('switch'), inclusief het login-scherm.
  useEffect(() => {
    if (mode !== 'superadmin') return;
    useThemeStore.getState().setMode('switch');
  }, [mode]);

  // Per-tenant tekst-overrides (ADR-0021) — publiek endpoint, geen
  // login nodig zodat overrides ook op het login-scherm gelden.
  useEffect(() => {
    if (mode !== 'tenant') return;
    void useLocaleOverridesStore.getState().fetchPublic();
  }, [mode]);

  if (consuming) {
    return <p style={{ padding: 'var(--space-6, 24px)' }}>{t('common.loading')}</p>;
  }

  return <AppRoutes />;
}
